/**
 * Ticket WS1: Öffentliche Übersicht aller kostenlosen Angebote.
 *
 * Die Liste wird bei jedem Aufruf aus dem Bestand gelesen: ein neu
 * veröffentlichtes Freebie erscheint von selbst, ein deaktiviertes verschwindet.
 * Nichts wird von Hand gepflegt, Entwürfe erscheinen nie.
 *
 * Die Adresse bleibt stabil, sie wird in Anzeigen verwendet. Die Navigation
 * bleibt in diesem Ticket unberührt.
 */

import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { LeadMagnetOverviewList } from "@/components/lead-magnets/LeadMagnetOverviewList";

import { listPublishedLeadMagnets } from "@/lib/lead-magnets.functions";
import { listPublishedWahlkalenderBundeslaender } from "@/lib/wahlkalender-landesseite.functions";
import type { WahlkalenderUebersichtEintrag } from "@/components/offers/offer-items";

const TITLE = "Kostenlose Downloads: Wahlkampf & Kommunikation | creategy";
const DESCRIPTION =
  "Praxisnahe Unterlagen aus über 200 Wahlkämpfen: Checklisten, Vorlagen und Werkzeuge von creategy. Kostenlos, ohne Verpflichtung.";

export const Route = createFileRoute("/wissen/downloads")({
  loader: async () => {
    const [items, wahlkalenderBundeslaender] = await Promise.all([
      listPublishedLeadMagnets(),
      listPublishedWahlkalenderBundeslaender(),
    ]);
    return { items, wahlkalenderBundeslaender: wahlkalenderBundeslaender as WahlkalenderUebersichtEintrag[] };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  errorComponent: DownloadsError,
  component: DownloadsPage,
});

function DownloadsError() {
  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1">Kostenlose Downloads</h1>
          <p className="mt-4 max-w-prose font-body text-base text-muted-foreground">
            Die Übersicht ist im Moment nicht abrufbar. Bitte laden Sie die Seite noch einmal – oder
            sprechen Sie uns direkt an.
          </p>
        </div>
      </section>
    </Layout>
  );
}

function DownloadsPage() {
  const { items, wahlkalenderBundeslaender } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <header className="max-w-3xl">
            <h1 className="heading-h1">Kostenlose Downloads</h1>
            <p className="mt-5 font-body text-lg leading-relaxed text-muted-foreground">
              Praxisnahe Unterlagen aus über 200 Wahlkämpfen.
            </p>
          </header>

          <div className="mt-12 lg:mt-16">
            <LeadMagnetOverviewList
              items={items}
              wahlkalenderBundeslaender={wahlkalenderBundeslaender}
            />
          </div>


        </div>
      </section>
    </Layout>
  );
}
